import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../contexts/AuthContext";
import { t, useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

// User's currency + symbol for localized prices (catalog is INR-based)
const useMoney = () => {
  const { user } = useAuth();
  const loc = user?.localization || {};
  return (amountInr) => fmtMoney(amountInr, loc.currency || "INR", loc.symbol || "₹");
};

const usePersistedState = (key, fallback) => {
  const [value, setValue] = useState(fallback);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    axios
      .get(`${API}/state`, headers())
      .then((r) => {
        if (r.data[key] !== undefined && (Array.isArray(r.data[key]) ? r.data[key].length : Object.keys(r.data[key]).length)) {
          setValue(r.data[key]);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [key]);
  const save = (next) => {
    setValue(next);
    axios.put(`${API}/state`, { [key]: next }, headers()).catch(() => {});
  };
  return [value, save, loaded];
};

export const FoodPanel = () => {
  const { add } = useCart();
  const lang = useLang();
  const money = useMoney();
  const [dishes, setDishes] = useState(null);
  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => setDishes(r.data.dishes)).catch(() => setDishes([]));
  }, []);
  const fallback = [
    { emoji: "🍔", name: "Smash Burger", chef_name: "@priyacooks", price: 89 },
    { emoji: "🍜", name: "Midnight Ramen", chef_name: "@kenjibowl", price: 79 },
    { emoji: "🌮", name: "Abuela Tacos", chef_name: "@leomakes", price: 99 },
  ];
  const meals = dishes && dishes.length ? dishes : fallback;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="panel-foodbite">
      {meals.map((m) => (
        <div key={m.id || m.name} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          {m.photo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${m.photo_url}`} alt={m.name} className="h-24 w-full rounded-xl border border-white/10 object-cover" />
          ) : (
            <span className="text-3xl">{m.emoji}</span>
          )}
          <p className="mt-3 text-sm font-bold">{m.name}</p>
          <p className="font-jbmono text-[10px] text-slate-500">{m.chef_name || m.chef}{m.description ? ` · ${m.description}` : ""}</p>
          <button
            data-testid={`panel-add-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => add({ ...m, kind: "food" })}
            className="mt-3 w-full rounded-full bg-[#F97316] py-2.5 font-jbmono text-xs font-bold text-white transition-transform hover:scale-[1.02]"
          >
            {money(m.price)} · {t(lang, "add")}
          </button>
        </div>
      ))}
      <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600 sm:col-span-3">
        {t(lang, "food_hint")}
      </p>
    </div>
  );
};

const COURSES = [
  { emoji: "🤖", name: "AI Skills", lessons: 12 },
  { emoji: "🎨", name: "Design Basics", lessons: 10 },
  { emoji: "🗣️", name: "Public Speaking", lessons: 8 },
];

export const LearnPanel = () => {
  const lang = useLang();
  const [progress, setProgress] = usePersistedState("learn", {});
  return (
    <div className="space-y-3" data-testid="panel-learnbite">
      {COURSES.map((c) => {
        const pct = progress[c.name] || 0;
        return (
          <div key={c.name} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <span className="text-2xl">{c.emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="flex justify-between text-sm">
                <span className="font-bold">{c.name}</span>
                <span className="font-jbmono text-xs text-[#06B6D4]">{pct}%</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/5">
                <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-1.5 font-jbmono text-[10px] text-slate-500">{c.lessons} {t(lang, "lessons")}</p>
            </div>
            <button
              data-testid={`learn-continue-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => {
                const next = Math.min(100, pct + 12);
                setProgress({ ...progress, [c.name]: next });
                if (next === 100) toast.success(`🏆 ${c.name} complete! Badge unlocked.`);
              }}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white"
            >
              {pct === 0 ? t(lang, "start") : pct >= 100 ? `🏆 ${t(lang, "done")}` : t(lang, "continue")}
            </button>
          </div>
        );
      })}
      <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
        {t(lang, "learn_hint")}
      </p>
    </div>
  );
};

const JOBS = [
  { company: "Google", job: "STEP Intern", pay: "₹80K/mo" },
  { company: "Amazon", job: "SDE Intern", pay: "₹70K/mo" },
  { company: "Microsoft", job: "Explorer Fellow", pay: "₹85K/mo" },
  { company: "Zeta", job: "Design Intern", pay: "₹40K/mo" },
];

export const CareerPanel = () => {
  const lang = useLang();
  const { user } = useAuth();
  const [applied, setApplied] = useState([]);
  const [busyJob, setBusyJob] = useState(null);

  // AI-style match score: overlap between the job and the user's profile signals
  const matchScore = (j) => {
    const mine = `${user.profile?.skills || ""} ${user.profile?.interests || ""} ${user.profile?.course || ""} ${user.profile?.branch || ""} ${user.role || ""}`.toLowerCase();
    const tokens = `${j.job} ${j.company}`.toLowerCase().split(/[^a-z]+/);
    const hits = tokens.filter((tk) => tk.length > 3 && mine.includes(tk)).length;
    return Math.min(97, 55 + hits * 10 + (mine.trim() ? 7 : 0));
  };

  useEffect(() => {
    axios.get(`${API}/applications/mine`, headers()).then((r) => {
      setApplied(r.data.applications.map((a) => `${a.company}|${a.job}`));
    }).catch(() => {});
  }, []);

  const apply = async (j) => {
    setBusyJob(j.job);
    try {
      const { data } = await axios.post(`${API}/applications`, { job: j.job, company: j.company }, headers());
      setApplied((a) => [...new Set([...a, `${j.company}|${j.job}`])]);
      toast.success(data.already ? "Already applied — you're in the pile." : `Applied to ${j.company} 🎉`);
    } catch {
      toast.error("Application failed. Try again.");
    } finally {
      setBusyJob(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="panel-careerbite">
      {JOBS.map((j) => {
        const isApplied = applied.includes(`${j.company}|${j.job}`);
        return (
          <div key={j.job} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] font-display text-base font-black">
              {j.company[0]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{j.job}</p>
              <p className="font-jbmono text-[10px] text-slate-500">
                {j.company} · {j.pay} · <span className="font-bold text-[#22C55E]" data-testid={`match-${j.company.toLowerCase()}`}>{matchScore(j)}% {t(lang, "match")}</span>
              </p>
            </div>
            <button
              data-testid={`apply-${j.company.toLowerCase()}`}
              onClick={() => apply(j)}
              disabled={isApplied || busyJob === j.job}
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-all ${
                isApplied ? "bg-[#06B6D4] text-[#09090B]" : "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white"
              } disabled:opacity-80`}
            >
              {isApplied ? t(lang, "applied") : t(lang, "apply")}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export const ShopPanel = () => {
  const { add } = useCart();
  const lang = useLang();
  const money = useMoney();
  const [products, setProducts] = useState(null);
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
  }, []);
  const fallback = [
    { emoji: "👟", name: "Campus Sneakers", price: 1299, category: "Fashion", stock: 12 },
    { emoji: "🎧", name: "Study Headphones", price: 1899, category: "Electronics", stock: 8 },
    { emoji: "📗", name: "DSA Crunch Book", price: 349, category: "Books", stock: 30 },
    { emoji: "🌿", name: "Organic Snack Box", price: 499, category: "Organic Products", stock: 20 },
  ];
  const items = products && products.length ? products : fallback;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="panel-shopbite">
      {items.map((p) => (
        <div key={p.id || p.name} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center">
          {p.photo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt={p.name} className="h-24 w-full rounded-xl border border-white/10 object-cover" />
          ) : (
            <span className="text-4xl">{p.emoji}</span>
          )}
          <p className="mt-2.5 text-sm font-bold">{p.name}</p>
          <p className="font-jbmono text-[10px] text-slate-500">{p.category}{p.vendor_name ? ` · ${p.vendor_name}` : ""}</p>
          <p className="font-jbmono text-xs text-[#22C55E]">{money(p.price)}</p>
          {p.stock === 0 ? (
            <p className="mt-3 w-full rounded-full border border-white/10 py-2 text-xs font-bold text-slate-500">{t(lang, "sold_out")}</p>
          ) : (
            <button
              data-testid={`shop-add-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => add({ ...p, kind: "shop", product_id: p.id })}
              className="mt-3 w-full rounded-full border border-white/15 py-2 text-xs font-bold text-slate-200 transition-colors hover:border-[#22C55E]/60 hover:bg-[#22C55E] hover:text-[#09090B]"
            >
              {t(lang, "to_cart")}{p.stock !== undefined && p.stock < 5 ? ` · ${p.stock} ${t(lang, "left")}` : ""}
            </button>
          )}
        </div>
      ))}
      <p className="col-span-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-600 sm:col-span-4">
        {t(lang, "shop_hint")}
      </p>
    </div>
  );
};

const TRIPS = [
  { emoji: "🏖️", name: "Goa Beach Week", price: "₹2,999", dates: "Dec 20–24" },
  { emoji: "🏔️", name: "Manali Snow Trip", price: "₹3,999", dates: "Jan 10–13" },
  { emoji: "🛕", name: "Thailand Edu Tour", price: "₹29,999", dates: "Mar 15–22" },
];

export const TravelPanel = () => {
  const lang = useLang();
  const [booked, setBooked] = usePersistedState("trips", []);
  return (
    <div className="space-y-3" data-testid="panel-travelbite">
      {TRIPS.map((tr) => {
        const isBooked = booked.includes(tr.name);
        return (
          <div key={tr.name} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <span className="text-3xl">{tr.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{tr.name}</p>
              <p className="font-jbmono text-[10px] text-slate-500">{tr.dates} · {tr.price}{t(lang, "per_person")}</p>
            </div>
            <button
              data-testid={`book-${tr.name.toLowerCase().split(" ")[0]}`}
              disabled={isBooked}
              onClick={() => {
                setBooked([...booked, tr.name]);
                toast.success(`Seat requested for ${tr.name} ✈️`);
              }}
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider ${
                isBooked ? "bg-[#06B6D4] text-[#09090B]" : "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white"
              }`}
            >
              {isBooked ? t(lang, "booked") : t(lang, "book")}
            </button>
          </div>
        );
      })}
      <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
        {t(lang, "travel_hint")}
      </p>
    </div>
  );
};

export const GreenPanel = () => {
  const lang = useLang();
  const [orders, setOrders] = useState(0);
  useEffect(() => {
    axios.get(`${API}/orders/mine`, headers()).then((r) => setOrders(r.data.orders.length)).catch(() => {});
  }, []);
  const stats = [
    { emoji: "🍲", label: t(lang, "green_meals"), value: orders },
    { emoji: "🌾", label: t(lang, "green_farmers"), value: orders },
    { emoji: "🌍", label: t(lang, "green_rescued"), value: Math.floor(orders / 2) },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="panel-greenbite">
      {stats.map((s) => (
        <div key={s.label} className="rounded-2xl border border-[#06B6D4]/20 bg-[#06B6D4]/[0.05] p-5 text-center">
          <span className="text-3xl">{s.emoji}</span>
          <p className="mt-2.5 font-display text-3xl font-black">{s.value}</p>
          <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
        </div>
      ))}
      <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600 sm:col-span-3">
        {t(lang, "green_hint")}
      </p>
    </div>
  );
};

const CLUBS = [
  { emoji: "🤖", name: "AI Builders" },
  { emoji: "🍔", name: "Food Lovers" },
  { emoji: "🚀", name: "Startup Club" },
  { emoji: "💼", name: "Placement Club" },
  { emoji: "✈️", name: "Travel Club" },
  { emoji: "🎨", name: "Designers" },
];

export const CommunityPanel = () => {
  const lang = useLang();
  const [joined, setJoined] = usePersistedState("clubs", []);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="panel-communities">
      {CLUBS.map((c) => {
        const isIn = joined.includes(c.name);
        return (
          <div key={c.name} className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center">
            <span className="text-3xl">{c.emoji}</span>
            <p className="mt-2.5 text-sm font-bold">{c.name}</p>
            <button
              data-testid={`join-${c.name.toLowerCase().replace(" ", "-")}`}
              onClick={() => {
                setJoined(isIn ? joined.filter((j) => j !== c.name) : [...joined, c.name]);
                if (!isIn) toast.success(`Welcome to ${c.name}!`);
              }}
              className={`mt-3 rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                isIn ? "bg-[#06B6D4] text-[#09090B]" : "border border-white/15 text-slate-200 hover:border-[#06B6D4]/60"
              }`}
            >
              {isIn ? t(lang, "joined") : t(lang, "join")}
            </button>
          </div>
        );
      })}
    </div>
  );
};
